import { createSelector } from 'reselect';
import _ from 'lodash';

import * as company from './company';

export { company };

// Properties
export const getProperties = state => state.datastore.properties;
export const getProperty = (state, propertyId) => _.get(getProperties(state), propertyId);
export const getSortedProperties = createSelector(
  getProperties,
  properties => _.sortBy(properties, ['display']) || [],
);

// Account
export const getSummaryCounts = state => state.datastore.summaryCounts;

// Tasks
export const getTasks = state => state.datastore.tasks;
export const getTask = (state, taskId) => _.get(getTasks(state), taskId);

export const getTaskOffers = state => state.datastore.taskOffers;

export const getTaskDrafts = state => state.datastore.taskDrafts;
export const getTaskDraft = (state, taskDraftId) => _.get(getTaskDrafts(state), taskDraftId);

const getAllTaskComments = state => state.datastore.taskComments;
export const getTaskComments = (state, taskId) => _.get(getAllTaskComments(state), taskId, null);

const getAllTaskPhotos = state => state.datastore.taskPhotos;
export const getTaskPhotos = (state, taskId) => _.get(getAllTaskPhotos(state), taskId, null);

const getAllTaskSeries = state => state.datastore.taskSeries;
export const getTaskSeries = (state, taskSeriesId) =>
  _.get(getAllTaskSeries(state), taskSeriesId, null);

export const getTaskTemplates = state => state.datastore.taskTemplates;
export const getTaskTemplate = (state, templateId) => _.get(getTaskTemplates(state), templateId);

export const getTaskAssignedPeople = createSelector(
  getTasks,
  getTaskDrafts,
  company.getPeople,
  (tasks, taskDrafts, people) =>
    _.chain({ ...tasks, ...taskDrafts })
      .map(task => _.get(task, 'people') || [])
      .flatten()
      .uniqBy('company_people_id')
      .map(assignment => _.get(people, _.get(assignment, 'company_people_id')))
      .compact()
      .value(),
);

export const getTaskCompanyId = (state, taskId) => _.get(getTask(state, taskId), 'company.id');

export const isTaskOwner = (state, taskId) =>
  _.isEqual(company.getCompanyId(state), getTaskCompanyId(state, taskId));

export const isTaskOwnerByCompanyId = (state, taskCompanyId) =>
  _.isEqual(company.getCompanyId(state), taskCompanyId);

// Reservations
export const getReservations = state => state.datastore.reservations;
export const getReservation = (state, reservationId) =>
  _.get(getReservations(state), reservationId);

// Types
const getAllTypes = state => state.datastore.types;

export const getTypes = (state, type) => _.get(getAllTypes(state), type);

export const getStates = state => getTypes(state, 'states');
export const getCountries = state => getTypes(state, 'countries');

export const getStatesByCountry = createSelector(
  getStates,
  states => _.groupBy(states, 'type_country'),
);

export const getCountriesByCode = createSelector(
  getCountries,
  countries => _.keyBy(countries, 'code'),
);

// Property Element Types
export const getElementTypes = state => getAllTypes(state).elements;

const getFeatureDefaultList = (state, feature, category = null) =>
  category && _.get(getTypes(state, 'feature_defaults'), category);

const getFeatureList = (state, feature) => _.get(getTypes(state, 'features'), feature);

export const getBulkFeatureElements = createSelector(
  [getFeatureList, getFeatureDefaultList],
  (elements, defaultElements) =>
    (elements && _.sortBy(_.filter(elements, element => element.feature), 'name')) ||
    (defaultElements && _.sortBy(_.filter(defaultElements, element => element.feature), 'name')),
);

export const getDepartments = state => getTypes(state, 'departments');
export const getElementIcon = (state, code) =>
  code ? _.get(_.find(_.get(state, 'datastore.types.elements', []), { code }, {}), 'icon') : null;

export const getSavedViews = (state, type) => {
  const savedViews = _.get(state, `app.savedViews[${type}]`);
  return (
    savedViews &&
    !_.isEmpty(savedViews) &&
    _.map(savedViews, element => ({
      category: element.category,
      data: element.data,
      label: element.name,
      module: element.module,
      private: element.private,
      value: element.id,
    }))
  );
};
